import React, { useEffect, useState, useRef, forwardRef, useImperativeHandle } from 'react';
import Modal from './Modal';
import { buildApiUrl } from '../../utils/apiUtils';

/**
 * BeGateway Payment component that can be triggered from an external button
 * 
 * @param {Object} props
 * @param {string} props.orderId - Unique order identifier
 * @param {number} props.amount - Payment amount
 * @param {string} props.currency - Currency code (EUR, USD, etc)
 * @param {string} props.description - Payment description
 * @param {Object} props.customer - Customer information
 * @param {function} props.onSuccess - Callback for successful payment
 * @param {function} props.onError - Callback for payment errors
 * @param {boolean} props.hideButton - Whether to hide the default payment button
 */
const BeGatewayPayment = forwardRef(({
    orderId,
    amount,
    currency = 'EUR',
    description = 'Star Map Purchase',
    customer = {},
    onSuccess,
    onError,
    hideButton = false
}, ref) => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [debugInfo, setDebugInfo] = useState(null);
    const [paymentToken, setPaymentToken] = useState(null);
    const [scriptLoaded, setScriptLoaded] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [widgetError, setWidgetError] = useState(false);
    const [paymentSuccessful, setPaymentSuccessful] = useState(false);
    const [transactionData, setTransactionData] = useState(null);
    const containerRef = useRef(null);
    const widgetContainerId = 'begateway-widget-container';

    // Expose methods to parent component via ref
    useImperativeHandle(ref, () => ({
        openPaymentModal: () => {
            if (!scriptLoaded || !paymentToken) {
                console.error('Payment system not ready yet');
                return false;
            }
            handleOpenModal();
            return true;
        },
        isReady: () => {
            return scriptLoaded && paymentToken !== null && !loading;
        },
        getPaymentUrl: () => {
            return paymentToken?.redirect_url || null;
        }
    }));

    // Get payment token from the server
    useEffect(() => {
        const fetchPaymentToken = async () => {
            try {
                setLoading(true);
                const paymentData = {
                    amount,
                    currency,
                    description,
                    order_id: orderId,
                    customer,
                    iframe_mode: hideButton
                };

                console.log('Attempting to fetch payment token with data:', paymentData);

                // Try multiple API endpoints for maximum reliability
                let response;
                let lastError;

                const endpoints = [
                    // Primary: API subdomain
                    window.location.hostname === 'localhost'
                        ? '/api/payment/create-token'
                        : 'https://api.interstellarmap.com/payment/create-token',
                    // Fallback: Direct endpoint on main domain
                    window.location.hostname === 'localhost'
                        ? '/api/public/cors-payment-fix.php'
                        : '/api/public/cors-payment-fix.php'
                ];

                for (const endpoint of endpoints) {
                    try {
                        console.log(`Trying payment endpoint: ${endpoint}`);

                        response = await fetch(endpoint, {
                            method: 'POST',
                            headers: {
                                'Content-Type': 'application/json',
                            },
                            body: JSON.stringify(paymentData),
                        });

                        // If we get a response (even if not ok), break the loop
                        if (response) {
                            console.log(`Payment endpoint ${endpoint} responded with status: ${response.status}`);
                            break;
                        }
                    } catch (error) {
                        console.warn(`Payment endpoint ${endpoint} failed:`, error.message);
                        lastError = error;
                        continue;
                    }
                }

                // If no endpoint worked, throw the last error
                if (!response) {
                    throw lastError || new Error('All payment endpoints failed');
                }

                console.log('Payment token response status:', response.status);

                // Store raw response text for debugging
                const responseText = await response.text();
                console.log('Raw response:', responseText);

                // Try to parse as JSON
                let data;
                try {
                    data = JSON.parse(responseText);
                    console.log('Payment token response data:', data);
                } catch (parseError) {
                    console.error('Error parsing JSON response:', parseError);
                    console.error('Response that failed to parse:', responseText);

                    // Check if response looks like HTML (common in production errors)
                    if (responseText.trim().startsWith('<')) {
                        throw new Error('Server returned HTML instead of JSON. This usually indicates a server configuration error or PHP error.');
                    } else if (responseText.trim() === '') {
                        throw new Error('Server returned empty response. Check server logs for errors.');
                    } else {
                        throw new Error('Invalid response from payment server: ' + responseText.substring(0, 100) + '...');
                    }
                }

                if (!response.ok) {
                    // Store debug info for display
                    setDebugInfo({
                        requestData: paymentData,
                        responseStatus: response.status,
                        responseData: data
                    });
                    throw new Error(data.message || data.error || 'Failed to create payment token');
                }

                // Modify the redirect URL to use our iframe-specific success page
                let modifiedData = { ...data };
                if (data.redirect_url) {
                    // Use a unique token pattern in the URL to help identify successful redirects
                    const uniqueToken = `payment-complete-${Date.now()}`;
                    modifiedData.redirect_url = data.redirect_url.replace(
                        /\/payment\/success/,
                        `/payment-iframe-success?token=${uniqueToken}`
                    );
                    console.log('Modified redirect URL:', modifiedData.redirect_url);

                    // Store the token for later validation
                    window.paymentCompleteToken = uniqueToken;
                }

                // Store payment token data
                setPaymentToken(modifiedData);
                setLoading(false);
            } catch (err) {
                console.error('Error creating payment token:', err);
                setError(err.message || 'Failed to initialize payment');
                setLoading(false);
                if (onError) onError(err);
            }
        };

        if (orderId && amount) {
            fetchPaymentToken();
        } else if (!orderId || !amount) {
            setError('Missing required payment information');
        }
    }, [orderId, amount, currency, description, customer, onError, hideButton]);

    // Load BeGateway script
    useEffect(() => {
        if (scriptLoaded || document.getElementById('begateway-script')) {
            return;
        }

        // Remove any existing script
        const existingScript = document.getElementById('begateway-script');
        if (existingScript) {
            existingScript.remove();
        }

        const script = document.createElement('script');
        script.id = 'begateway-script';
        script.src = 'https://js.begateway.com/begateway-1-latest.min.js';
        script.async = true;

        script.onload = () => {
            console.log('BeGateway script loaded successfully');
            setScriptLoaded(true);
        };

        script.onerror = (err) => {
            console.error('Error loading BeGateway script:', err);
            setWidgetError(true);
        };

        document.body.appendChild(script);

        return () => {
            // Clean up on unmount
            if (document.body.contains(script)) {
                document.body.removeChild(script);
            }
        };
    }, []);

    // Add event listener for successful payment redirects
    useEffect(() => {
        const handlePaymentSuccess = (event) => {
            // Skip irrelevant messages
            if (!event.data || event.data.target === 'metamask-inpage') {
                return;
            }

            try {
                console.log('Received potential payment success event:', event);

                // Check for success URL in the origin
                if (typeof event.origin === 'string' &&
                    (event.origin.includes('begateway') ||
                        event.origin.includes(window.location.host))) {

                    console.log('Received message from payment gateway or our site');

                    // Check if it's a data object with a payment status
                    if (typeof event.data === 'object' && event.data.status) {
                        handlePaymentStatusUpdate(event.data);
                        return;
                    }

                    // Check if it's a string that can be parsed as JSON
                    if (typeof event.data === 'string') {
                        try {
                            const data = JSON.parse(event.data);
                            if (data.status) {
                                handlePaymentStatusUpdate(data);
                                return;
                            }
                        } catch (e) {
                            // Not JSON, continue with other checks
                        }
                    }

                    // Check if the URL contains success indicators
                    if (typeof event.data === 'string' &&
                        (event.data.includes('payment/success') ||
                            event.data.includes('payment-complete') ||
                            event.data.includes('payment-iframe-success'))) {

                        console.log('Payment success detected via URL!');
                        handlePaymentStatusUpdate({
                            status: 'successful',
                            transaction_id: orderId
                        });
                    }
                }
            } catch (err) {
                console.error('Error processing potential payment success event:', err);
            }
        };

        const handlePaymentStatusUpdate = (data) => {
            if (data.status === 'successful') {
                console.log('Payment successful, updating UI');

                // Set success state and store transaction data
                setPaymentSuccessful(true);
                setTransactionData(data);

                // Clear the iframe container
                const container = document.getElementById(widgetContainerId);
                if (container) {
                    container.innerHTML = '';
                }

                // Update order status in database
                if (orderId) {
                    console.log('Updating order status for order:', orderId);
                    fetch(buildApiUrl('orders/update-status'), {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                        },
                        body: JSON.stringify({
                            order_id: orderId,
                            status: 'completed',
                            payment_ref: data.transaction_id || 'payment-success'
                        })
                    })
                        .then(response => response.json())
                        .then(result => {
                            console.log('Order status update result:', result);
                        })
                        .catch(err => {
                            console.error('Error updating order status:', err);
                        });
                }

                // Clear cart after successful payment
                localStorage.setItem('cart', '[]');
                window.dispatchEvent(new Event('cartUpdated'));

                // Call success callback
                if (onSuccess) {
                    onSuccess(data);
                }

                // Redirect to profile page after a short delay
                setTimeout(() => {
                    window.location.href = '/profile';
                }, 1500);
            } else if (data.status === 'failed' || data.status === 'cancelled') {
                console.log('Payment failed or cancelled:', data.status);

                if (onError) {
                    onError(new Error(data.message || `Payment ${data.status}`));
                }

                setIsModalOpen(false);
            }
        };

        window.addEventListener('message', handlePaymentSuccess);

        return () => {
            window.removeEventListener('message', handlePaymentSuccess);
        };
    }, [orderId, onSuccess, onError, widgetContainerId]);

    // Handle opening the payment modal (only for non-embedded mode)
    const handleOpenModal = () => {
        setIsModalOpen(true);
        setWidgetError(false); // Reset any previous errors
    };

    // Handle direct link as fallback
    const handleDirectLinkClick = (e) => {
        e.preventDefault();

        if (!paymentToken || !paymentToken.redirect_url) {
            console.error('Cannot navigate to payment: missing redirect URL');
            setError('Payment URL not available. Please try again.');
            return;
        }

        // Open in new tab
        window.open(paymentToken.redirect_url, '_blank', 'noopener,noreferrer');
    };

    // Initialize payment form directly in the container (without modal)
    const initializePaymentForm = () => {
        try {
            // Debugging - check BeGateway availability
            console.log('Checking BeGateway constructor availability:', typeof window.BeGateway);
            console.log('Window object has BeGateway?', 'BeGateway' in window);

            // Wait a short moment to ensure the BeGateway script is fully loaded
            setTimeout(() => {
                try {
                    // Check if BeGateway constructor exists
                    if (typeof window.BeGateway !== 'function') {
                        console.error('BeGateway constructor not found - attempting to reinitialize');

                        // Force reload of BeGateway script
                        const existingScript = document.getElementById('begateway-script');
                        if (existingScript) {
                            existingScript.remove();
                        }

                        const script = document.createElement('script');
                        script.id = 'begateway-script';
                        script.src = 'https://js.begateway.com/begateway-1-latest.min.js';
                        script.async = true;

                        script.onload = () => {
                            console.log('BeGateway script reloaded successfully');
                            // Try initializing again after script loads
                            setTimeout(() => {
                                initializeBeGatewayWidget();
                            }, 500);
                        };

                        document.body.appendChild(script);
                        return;
                    }

                    initializeBeGatewayWidget();
                } catch (innerErr) {
                    console.error('Error in delayed initialization:', innerErr);
                    setWidgetError(true);
                }
            }, 500);
        } catch (err) {
            console.error('Error in initializePaymentForm:', err);
            setWidgetError(true);
        }
    };

    // Separate function to actually initialize the BeGateway widget
    const initializeBeGatewayWidget = () => {
        try {
            console.log('Creating inline BeGateway payment form with URL:', paymentToken.redirect_url);

            // First clear the container
            const container = document.getElementById(widgetContainerId);
            if (!container) {
                console.error('Widget container not found:', widgetContainerId);
                setWidgetError(true);
                return;
            }

            // Clear the container
            container.innerHTML = '';

            // Get the redirect URL
            const redirectUrl = paymentToken.redirect_url;
            if (!redirectUrl) {
                console.error('Missing redirect URL in payment token');
                setWidgetError(true);
                return;
            }

            // Create iframe directly rather than using BeGateway widget constructor
            // This gives us more control over the iframe
            const iframe = document.createElement('iframe');
            iframe.src = redirectUrl;
            iframe.style.width = '100%';
            iframe.style.height = '500px';
            iframe.style.border = 'none';
            iframe.style.display = 'block';
            iframe.style.borderRadius = '8px';
            iframe.setAttribute('allowpaymentrequest', 'true');
            iframe.setAttribute('allow', 'payment');
            iframe.setAttribute('frameborder', '0');
            iframe.setAttribute('id', 'begateway-payment-iframe');

            // Add the iframe to the container
            container.appendChild(iframe);
            console.log('Payment iframe created and added to container');

            // Don't try to access the iframe content directly - it will cause cross-origin errors
            // Just add an event listener for messages from the iframe instead
        } catch (err) {
            console.error('Error initializing BeGateway widget:', err);
            setWidgetError(true);
        }
    };

    // Auto-open payment modal when payment is ready (if hideButton is true)
    useEffect(() => {
        // When the component is ready and we're in embedded mode (hideButton=true)
        if (hideButton && scriptLoaded && paymentToken !== null && !loading && !paymentSuccessful) {
            // For embedded mode, don't open a modal but initialize the payment form directly
            initializePaymentForm();
        } else if (!hideButton && scriptLoaded && paymentToken !== null && !loading && !isModalOpen) {
            // Only open modal for non-embedded mode
            handleOpenModal();
        }
    }, [scriptLoaded, paymentToken, loading, hideButton, isModalOpen, paymentSuccessful]);

    // Render success message UI when payment is successful
    const renderSuccessMessage = () => {
        // Return null instead of success message - let the parent component handle success display
        return null;
    };

    if (error) {
        return (
            <div className="bg-red-900/20 border border-red-500 rounded-lg p-4 text-center">
                <p className="text-red-400">{error}</p>

                {debugInfo && (
                    <div className="mt-4 text-left bg-gray-900/50 p-3 rounded text-xs text-gray-300 overflow-auto max-h-40">
                        <h4 className="font-bold mb-1">Debug Information:</h4>
                        <p>Request Data:</p>
                        <pre>{JSON.stringify(debugInfo.requestData, null, 2)}</pre>
                        <p>Response Status: {debugInfo.responseStatus}</p>
                        <pre>{JSON.stringify(debugInfo.responseData, null, 2)}</pre>
                    </div>
                )}

                <button
                    className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg"
                    onClick={() => window.location.reload()}
                >
                    Try Again
                </button>
            </div>
        );
    }

    // For the embedded widget container
    if (hideButton) {
        return (
            <div className="payment-container w-full" ref={containerRef}>
                {loading ? (
                    <div className="text-center py-10">
                        <div className="inline-block animate-spin rounded-full h-10 w-10 border-b-2 border-purple-500"></div>
                        <p className="mt-4 text-lg text-gray-300">Initializing payment gateway...</p>
                    </div>
                ) : paymentSuccessful ? (
                    renderSuccessMessage()
                ) : (
                    <div className="flex flex-col items-center">
                        <div id={widgetContainerId} className="payment-widget-container w-full bg-white rounded-lg" style={{ width: '100%', minHeight: '500px', maxWidth: '100%', overflow: 'hidden' }}>
                            <div className="flex flex-col items-center justify-center h-full w-full text-center p-6">
                                <div className="inline-block animate-spin rounded-full h-10 w-10 border-b-2 border-purple-500 mb-4"></div>
                                <p className="text-lg text-gray-700 mb-2">Loading payment form...</p>
                                <p className="text-sm text-gray-500">Please wait while we connect to the payment gateway.</p>
                            </div>
                        </div>

                        {/* Fallback payment button - only show if there's an error */}
                        {widgetError && (
                            <div className="mt-6 text-center">
                                <p className="mb-4 text-sm text-gray-400">If the payment form doesn't appear, please use the button below:</p>
                                <button
                                    onClick={(e) => {
                                        e.preventDefault();
                                        if (paymentToken?.redirect_url) {
                                            window.open(paymentToken.redirect_url, '_blank', 'noopener,noreferrer');
                                        }
                                    }}
                                    className="px-6 py-3 bg-purple-600 hover:bg-purple-700 text-white font-medium rounded-lg transition-colors"
                                >
                                    Open Payment Form
                                </button>
                            </div>
                        )}
                    </div>
                )}
            </div>
        );
    }

    // Non-embedded mode (with buttons)
    return (
        <div className="payment-container w-full" ref={containerRef}>
            {loading ? (
                <div className="text-center py-10">
                    <div className="inline-block animate-spin rounded-full h-10 w-10 border-b-2 border-purple-500"></div>
                    <p className="mt-4 text-lg text-gray-300">Initializing payment gateway...</p>
                </div>
            ) : (
                <div className="payment-form-container py-4 space-y-4">
                    <button
                        onClick={handleOpenModal}
                        className="w-full py-3 px-4 bg-purple-600 hover:bg-purple-700 text-white font-medium rounded-lg text-center transition-colors"
                        disabled={!scriptLoaded}
                    >
                        Complete Payment
                    </button>

                    <div className="flex justify-center">
                        <a
                            href="#"
                            onClick={handleDirectLinkClick}
                            className="text-sm text-gray-400 hover:text-white underline"
                        >
                            Open payment in new tab
                        </a>
                    </div>

                    <p className="text-center text-gray-300 mt-2">
                        Your payment is secured using industry-standard encryption.
                    </p>

                    {/* Modal for non-embedded mode */}
                    <Modal
                        isOpen={isModalOpen}
                        onClose={() => setIsModalOpen(false)}
                        title="Complete Your Payment"
                        size="xl"
                    >
                        {widgetError ? (
                            <div className="text-center py-8">
                                <p className="text-yellow-400 mb-6">
                                    There was a problem loading the payment form. Please try using the direct payment link.
                                </p>
                                <button
                                    onClick={handleDirectLinkClick}
                                    className="py-3 px-6 bg-purple-600 hover:bg-purple-700 text-white font-medium rounded-lg transition-colors"
                                >
                                    Continue to Payment in New Tab
                                </button>
                            </div>
                        ) : paymentSuccessful ? (
                            renderSuccessMessage()
                        ) : (
                            <div id={widgetContainerId} className="payment-widget-container w-full bg-white rounded-lg" style={{ width: '100%', minHeight: '500px', maxWidth: '100%', overflow: 'hidden' }}>
                                <div className="flex flex-col items-center justify-center h-full w-full text-center p-6">
                                    <div className="inline-block animate-spin rounded-full h-10 w-10 border-b-2 border-purple-500 mb-4"></div>
                                    <p className="text-lg text-gray-700 mb-2">Loading payment form...</p>
                                    <p className="text-sm text-gray-500">Please wait while we connect to the payment gateway.</p>
                                </div>
                            </div>
                        )}
                    </Modal>
                </div>
            )}
        </div>
    );
});

export default BeGatewayPayment; 